import React, {
  useState,
  useCallback,
  useEffect,
  Fragment,
  useMemo,
  useRef,
} from "react";
import { Switch } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  getCurrentUser,
  getDepartments,
  isAuditee,
  isUser,
} from "@neumetric/blueprint-lib/store";
import { AuditHeader, Evidence } from "./Header";
import {
  auditActions,
  getCurrentAudit,
  getCurrentControl,
  getPriorityById,
  canUpdateAudit,
  shouldDisableAction,
} from "../../store";
import {
  Col,
  Row,
  NMForm,
  TextButton,
  HistoryList,
  Button,
  OutlinedButton,
  NMWaitIndicator,
  NMFormPopup,
  useSub,
  usePub,
} from "@neumetric/blueprint-lib/components";

const FINDINGS_DEFAULT_VALUE = "No specific Findings for this Control.";
const OBSERVATION_DEFAULT_VALUE = "No specific Observations for this Control.";
const ControlFilterFields = [
  {
    attribute: "group",
    typeRepresentation: "DROPDOWN",
    placeholder: "Group",
    allowClear: true,
    classes: { root: "w-100 mb-4" },
  },
  {
    attribute: "section",
    typeRepresentation: "DROPDOWN",
    placeholder: "Section",
    allowClear: true,
    classes: { root: "w-100 mb-0" },
  },
];

const InfoPopupFields = [
  {
    attribute: "dept_name",
    typeRepresentation: "TEXT",
    label: "Department:",
    readOnly: true,
    labelAlign: "middle",
    classes: { root: "w-50 mb-[1rem]", label: "w-[15rem]" },
  },
  {
    attribute: "ctrl_status",
    typeRepresentation: "DROPDOWN",
    label: "Status:",
    readOnly: true,
    template: "DOT",
    labelAlign: "middle",
    classes: { root: "w-50 mb-[1rem]", label: "w-[5_5rem]" },
  },
  {
    attribute: "desc",
    typeRepresentation: "TEXT",
    placeholder: "Description",
    label: "Description:",
    readOnly: true,
    classes: {
      root: "w-100 mb-[1rem]",
      label: "w-[15rem]",
      input: "bg-contrast",
    },
  },
  {
    attribute: "evidence_expected",
    typeRepresentation: "TEXT",
    label: "Evidence Expected:",
    readOnly: true,
    classes: { root: "w-100 mb-[1rem]", label: "w-[15rem]" },
  },
  {
    attribute: "recommendation_value",
    typeRepresentation: "TEXT",
    label: "Recommendation:",
    readOnly: true,
    classes: { root: "w-100 mb-[1rem]", label: "w-[15rem]" },
  },
  {
    attribute: "external_ref",
    typeRepresentation: "HTML",
    label: "Reference(s):",
    readOnly: true,
    classes: { root: "w-100 mb-[1rem]", label: "w-[15rem]", input: "fs-16" },
  },
];

const ResultFormFields = [
  {
    attribute: "report_included",
    typeRepresentation: "CUSTOM",
    component: null,
  },
  {
    attribute: "gapscore",
    typeRepresentation: "TEXT",
    placeholder: "10",
    label: "Gap Score",
    labelAlign: "middle",
    disabled: true,
    classes: { root: "w-43 mb-12 gap-score-field" },
    getClasses: (value) => {
      const classes = { input: "bg-contrast", label: "w-74-px" };
      if (value) {
        const gapScore = Number(value);
        if (gapScore <= 10 && gapScore > 5) {
          classes.input += " score-urgent";
        } else if (gapScore <= 5 && gapScore > 0) {
          classes.input += " score-complete";
        } else {
          classes.input += " score-warning";
        }
      }
      return classes;
    },
  },
  {
    attribute: "status",
    typeRepresentation: "CUSTOM",
    component: null,
  },
  {
    attribute: "result_value",
    typeRepresentation: "DROPDOWN",
    placeholder: "Result",
    label: "Result",
    labelAlign: "middle",
    classes: { root: "w-100 mb-12", label: "w-74-px", input: "bg-contrast" },
  },
  {
    className: "flex-wrap w-100 mb-12 pos-relative",
    childrenFields: [
      {
        attribute: "observation_in_history",
        typeRepresentation: "CHECKBOX",
        label: "Include in History Note",
        classes: {
          root: "mb-0 pos-absolute include-checkbox",
          input: "",
          label: "fs-14",
        },
      },
      {
        attribute: "assessor_observation",
        typeRepresentation: "TEXTAREA",
        label: "Observation",
        rows: 4,
        layout: "vertical",
        placeholder: OBSERVATION_DEFAULT_VALUE,
        classes: { root: "w-100 mb-0", input: "bg-contrast" },
      },
    ],
  },
  {
    className: "flex-wrap w-100 mb-12 pos-relative",
    childrenFields: [
      {
        attribute: "findings_in_history",
        typeRepresentation: "CHECKBOX",
        label: "Include in History Note",
        classes: {
          root: "mb-0 pos-absolute include-checkbox",
          input: "",
          label: "fs-14",
        },
      },
      {
        attribute: "assessor_findings",
        typeRepresentation: "TEXTAREA",
        label: "Findings",
        rows: 4,
        layout: "vertical",
        placeholder: FINDINGS_DEFAULT_VALUE,
        classes: { root: "w-100 mb-0", input: "bg-contrast" },
      },
    ],
  },
];

const ControlList = (props) => {
  const { controls, initialControlId } = props;
  const _options = useRef({ groups: {}, sections: {} });
  const dispatch = useDispatch();
  const [state, setState] = useState({ selected: initialControlId });
  const disableAction = useSelector(shouldDisableAction);
  const currentControl = useSelector(getCurrentControl);

  useEffect(() => {
    if (initialControlId !== state.selected) {
      // setState((_) => ({ ..._, selected: initialControlId }));
    }
  }, [initialControlId]);

  useEffect(() => {
    if (currentControl && currentControl.id !== state.selected) {
      setState((_) => ({ ..._, selected: currentControl.id }));
    }
  }, [currentControl]);

  useEffect(() => {
    _options.current = { groups: {}, sections: {} };
    controls.forEach((control) => {
      if (control.group) {
        _options.current.groups[control.group.id] = {
          id: control.group.id,
          label: control.group.title,
        };
      }
      if (control.section) {
        _options.current.sections[control.section.id] = {
          id: control.section.id,
          label: control.section.title,
          groupId: control.group.id,
        };
      }
    });
    setState((_) => ({ ..._, group: null, section: null }));
  }, [controls]);

  const handleControlSelect = useCallback(
    (controlId) => {
      if (currentControl && currentControl.id === controlId) return;
      setState((_) => ({ ..._, selected: controlId }));
      dispatch(auditActions.setCurrentControl(null));
      dispatch(auditActions.fetchControl({ controlId }));
    },
    [currentControl],
  );

  const getOptions = useCallback(
    (attribute) => {
      if (attribute === "group") {
        return Object.values(_options.current.groups);
      }
      if (attribute === "section") {
        const options = Object.values(_options.current.sections);
        if (state.group) {
          return options.filter((option) => option.groupId === state.group);
        }
        return options;
      }
      return [];
    },
    [state],
  );

  const handleChange = (attribute, value) => {
    const newState = { ...state };
    newState[attribute] = value;
    setState(newState);
  };

  const filteredControls = useMemo(() => {
    return controls.filter((control) => {
      const inGroup = state.group ? control.group.id === state.group : true;
      const inSection = state.section
        ? control.section.id === state.section
        : true;
      return inGroup && inSection;
    });
  }, [controls, state]);

  return (
    <Col className="w-11 h-100 overflow-hide bg-contrast controls-list">
      <Col className="w-100 card-shadow pl-12 pr-12 pt-8 pb-8 mb-10">
        <NMForm
          fields={ControlFilterFields}
          formData={{}}
          getOptions={getOptions}
          onChange={handleChange}
        />
        <span className="mt-8 fs-14 fw-400 text-filled ml-8">Control</span>
      </Col>
      <Col className="w-100 flex-auto overflow-auto nm-scroll-view">
        {filteredControls.map((control) => {
          const selected = state.selected === control.id;
          const readOnly = currentControl === null || disableAction;
          return (
            <Col
              key={control.id}
              onClick={() => handleControlSelect(control.id)}
              className={`w-100 control-item pl-20 pt-7 pb-7 ${selected ? "active pointer" : readOnly ? "disabled" : "pointer"}`}
            >
              <span
                className={`fw-400 fs-14 ${selected ? "text-filled" : readOnly ? "text-contrast" : "text-action"}`}
              >
                {control.title}
              </span>
            </Col>
          );
        })}
      </Col>
    </Col>
  );
};

const ControlQA = (props) => {
  const dispatch = useDispatch();
  const publish = usePub();
  const canEdit = useSelector(canUpdateAudit);
  const departments = useSelector(getDepartments);
  const currentControl = useSelector(getCurrentControl);
  const priority = getPriorityById(currentControl.priority);
  const [showMore, setShowMore] = useState(false);
  const [showInfo, setShowInfo] = useState(null);
  const initialAnswer = currentControl.asmt_cntrl_resp_options.find(
    (option) => option.state,
  );
  const [answer, setAnswer] = useState(initialAnswer ? initialAnswer.id : null);
  const mainOptions = currentControl.asmt_cntrl_resp_options.slice(0, 4);
  const restOptions = currentControl.asmt_cntrl_resp_options.slice(4);
  const status = Boolean(currentControl.is_answered) ? "responded" : "pending";
  const answerInRestOptions = useMemo(() => {
    return restOptions.find((option) => option.id === answer);
  }, [restOptions, answer]);

  const handleShowMore = useCallback(() => {
    setShowMore(!showMore);
  }, [answerInRestOptions, showMore]);

  const debouncedSendAnswer = useCallback(
    (value) => {
      const action = auditActions.updateControlResponse({
        controlId: currentControl.id,
        answer: value,
      });
      action.onComplete = () => {
        publish("UPDATE_AUDIT_FILTER");
      };
      dispatch(action);
    },
    [currentControl],
  );

  const handleOptionSelect = useCallback(
    (option) => {
      if (answer === option.id) {
        return;
      }
      setAnswer(option.id);
      setShowMore(false);
      debouncedSendAnswer(option.id);
    },
    [answer, debouncedSendAnswer],
  );

  const handleInfoClick = (value) => {
    setShowInfo(value);
  };

  const getOptions = useCallback(
    (attribute) => {
      if (attribute === "department") {
        return departments;
      }
      if (attribute === "ctrl_status") {
        const cntrlStatus = currentControl.ctrl_status;
        return [
          {
            label: cntrlStatus,
            id: cntrlStatus,
            dotIconName:
              cntrlStatus.toLowerCase() === "responded"
                ? "Closed"
                : cntrlStatus,
          },
        ];
      }
      return [];
    },
    [currentControl, departments],
  );

  const getComplaintText = (response) => {
    const optionTextMap = [
      ["not applicable", "Not Applicable"],
      ["auditee", "Control Exempted"],
      ["fully", "Fully compliant"],
      ["almost", "Almost compliant"],
      ["somewhat", "Somewhat compliant"],
      ["partial", "Partially implemented"],
      ["not known", "Compliance Unknown"],
      ["not", "Not implemented"],
      ["unclear", "Requirements Unclear"],
      ["tested", "Untested Control"],
      ["unknown", "Compliance Unknown"],
      ["skipped", "Skipped Control"],
      ["redundant", "Redundant Control"],
    ];
    const normalizedResponse = response.toLowerCase();

    const matchedStatus = optionTextMap.find(([keyword]) =>
      normalizedResponse.includes(keyword),
    );

    return matchedStatus ? matchedStatus[1] : response;
  };

  const getOptionElement = useCallback(
    (option) => {
      const isSelected = option.id === answer;
      const optionText = getComplaintText(option.txt);
      const optionCSSClass = option.txt
        ? optionText.toLowerCase().replaceAll(" ", "-")
        : "";
      return (
        <Row
          className={`w-160-px h-32-px border-radius option overflow-hidden pointer ${optionCSSClass} ${isSelected ? "selected" : ""} ${canEdit ? "" : "non-clickable"}`}
          align="center"
          justify="center"
          title={option.txt}
          onClick={() => canEdit && handleOptionSelect(option)}
        >
          <span
            className={`fs-14 fw-400 overflow-hidden ellipsis text-caps ${isSelected ? "text" : ""}`}
          >
            {optionText}
          </span>
        </Row>
      );
    },
    [canEdit, answer],
  );

  const getRestOptions = useCallback(() => {
    if (!answerInRestOptions) {
      return restOptions;
    }
    return [...restOptions].sort((optionA, optionB) => {
      if (optionA.id === answerInRestOptions.id) return -1;
      if (optionB.id === answerInRestOptions.id) return 1;
      return 0;
    });
  }, [restOptions, answerInRestOptions]);

  return (
    <Col className="w-100">
      <Row align="center" className="w-100" justify="between">
        <Row>
          <Col
            className={`pt-4 pb-4 pl-8 pr-8 border-radius bg-${priority.label.toLowerCase()}-box`}
          >
            <span className={`fs-12 fw-600`}>{priority.label}</span>
          </Col>
          <Col className="pt-4 pb-4 pl-8 pr-8 bg-heading ml-20 border-radius">
            <span className="fs-12 fw-400 text-contrast">
              {currentControl.dept_name}
            </span>
          </Col>
        </Row>
        <Row className="status-badge pl-8 pt-4 pb-4 pr-8" align="center">
          <span>Status:</span>
          <Col
            className={`pt-4 pb-4 pl-8 pr-8 bg-contrast ml-8 value ${status.toLowerCase()}`}
          >
            <span className="fs-12 fw-400 text-upper">{status}</span>
          </Col>
        </Row>
      </Row>
      <Row className="w-100 mt-8" justify="between">
        <span className="fs-18 fw-400 text-justify">
          <span className="fw-600 ">{currentControl.title}</span>:{" "}
          {currentControl.txt}
        </span>
        <TextButton
          icon="DOCUMENT_INFO"
          classes={{ icon: "fs-30", button: "ml-12" }}
          onClick={() => handleInfoClick(true)}
        />
      </Row>
      <Col className="w-100 mt-10 pa-10 answer-section flex-wrap">
        <Row className="w-100 first-row">
          {mainOptions.map((option, index) => {
            return getOptionElement(option, index);
          })}
        </Row>
        <Row className="w-100">
          <OutlinedButton
            icon="DOT_MENU"
            classes={{ icon: "fs-3 mt-10", button: "mr-10" }}
            onClick={handleShowMore}
          />
          <Row
            className="first-row rest-options slide overflow-hide flex-wrap flex-auto"
            style={{
              height: showMore
                ? Math.ceil(restOptions.length / 3) * 42
                : answerInRestOptions
                  ? 33
                  : 0,
            }}
          >
            {getRestOptions().map((option, index) => {
              return getOptionElement(option, index);
            })}
          </Row>
        </Row>
      </Col>
      {showInfo && (
        <NMFormPopup
          visible={true}
          width="814px"
          showClose="label"
          getOptions={getOptions}
          title={
            <span className="fs-15 text-filled fw-500">Control Details</span>
          }
          classes={{
            header: "bg-header pt-8 pb-8 nm-popup-header-shadow",
            content: "pt-12",
            root: "audit-cntrl-info",
          }}
          fields={InfoPopupFields}
          formData={currentControl}
          onModalClose={() => handleInfoClick(false)}
        />
      )}
    </Col>
  );
};

const History = () => {
  const canEdit = useSelector(canUpdateAudit);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const currentUser = useSelector(getCurrentUser);
  const dispatch = useDispatch();
  const currentControl = useSelector(getCurrentControl);

  const handleAddComment = (comment) => {
    const action = auditActions.addControlComment({
      controlId: currentControl.id,
      comment,
    });
    setIsSubmitting(true);
    action.onComplete = () => {
      setIsSubmitting(false);
    };
    dispatch(action);
  };

  return (
    <Col className="w-37 h-100 bg-contrast pr-15 pt-15 pb-15 overflow-hide">
      <HistoryList
        items={currentControl.comments}
        classes={{
          root: "w-100 h-90",
          comment: "fs-14",
          attachment: "fs-14",
          input: "fs-14",
        }}
        editableFlag={canEdit}
        currentUser={currentUser}
        allowAttachments={false}
        isSubmitting={isSubmitting}
        onSubmit={handleAddComment}
        emptyMessage={
          <span className="fs-14">There are no history right now!</span>
        }
        accept="image/*,application/pdf,.doc,.docx,.ppt,.pptx,.html,text/html,.eml,audio/*,video/*"
      />
    </Col>
  );
};

const Result = () => {
  const dispatch = useDispatch();
  const publish = usePub();
  const _formRef = useRef(null);
  const currentUser = useSelector(getCurrentUser);
  const canEdit = useSelector(canUpdateAudit);
  const currentAudit = useSelector(getCurrentAudit);
  const currentControl = useSelector(getCurrentControl);
  const [included, setIncluded] = useState(currentControl.report_findings);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [disabled, setDisabled] = useState(!canEdit);
  const status =
    (isUser(currentUser)
      ? currentControl.auditee_ctrl_status
      : currentControl.auditor_ctrl_status) || "Pending";
  React.useEffect(() => {
    if (included !== currentControl.report_findings) {
      setIncluded(currentControl.report_findings);
    }
  }, [currentControl]);

  React.useEffect(() => {
    if (currentControl) {
      _formRef.current?.updateFormData(getFormValues());
    }
  }, [currentControl]);

  const handleSwitchChange = useCallback(
    (checked) => {
      if (!canEdit) {
        return;
      }
      setIncluded(checked);
      setDisabled(false);
    },
    [canEdit],
  );

  const getFormValues = useCallback(() => {
    return {
      gapscore: currentControl.risk_score,
      result_value: currentControl.result_value,
      assessor_observation:
        currentControl.assessor_observation ||
        "No specific Observations for this Control.",
      assessor_findings:
        currentControl.assessor_findings ||
        "No specific Findings for this Control.",
      observation_in_history: true,
      findings_in_history: true,
    };
  }, [currentControl]);

  const getOptions = () => {
    return currentAudit.resultOptions;
  };

  const getStatusComponent = useCallback(() => {
    return (
      <Row
        className="w-50 status-badge pl-8 pt-4 pb-4 "
        align="center"
        justify="end"
      >
        <span className="fs-16 fw-500">Status:</span>
        <Col
          className={`pt-4 pb-4 pl-8 pr-8 bg-contrast ml-8 value ${status.toLowerCase()}`}
        >
          <span className="fs-12 fw-400 text-upper">{status}</span>
        </Col>
      </Row>
    );
  }, [canEdit, status]);

  const getReportComponent = useCallback(() => {
    return (
      <Row className="w-100 mb-10 report-switch" align="center" justify="end">
        <Switch
          checkedChildren="Reported"
          checked={included}
          disabled={!canEdit}
          unCheckedChildren="Excluded"
          onChange={handleSwitchChange}
        />
      </Row>
    );
  }, [included, canEdit, handleSwitchChange]);

  const getFormFields = useCallback(() => {
    const formFields = ResultFormFields.map((field) => {
      return { ...field, disabled: !canEdit };
    });
    formFields[0].component = getReportComponent();
    formFields[2].component = getStatusComponent();
    return formFields;
  }, [getReportComponent, status, canEdit]);

  const fetchResult = (result) => {
    const action = auditActions.fetchControlResult({
      controlId: currentControl.id,
      result,
    });
    action.onComplete = (res) => {
      if (res) {
        const { risk_score } = res;
        const gapScore = Number(risk_score);
        const includeInReport = gapScore >= 8 ? true : false;
        setIncluded(includeInReport);
        _formRef.current?.updateFormData({ gapscore: `${risk_score}` });
      }
    };
    dispatch(action);
  };

  const handleChange = useCallback(
    (attribute, value) => {
      if (canEdit) {
        if (attribute === "result_value") {
          fetchResult(value);
        }
        setDisabled(false);
      }
    },
    [canEdit],
  );
  const checkDefaultComments = useCallback(() => {
    return (
      (currentControl.comments || []).filter((comment) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(comment.comment, "text/html");
        const text = (doc.body.textContent || "")
          .replace(/\s+/g, " ") // normalize spaces
          .trim();
        return (
          text.includes(FINDINGS_DEFAULT_VALUE) &&
          text.includes(OBSERVATION_DEFAULT_VALUE)
        );
      }).length === 0
    );
  }, [currentControl]);

  const addComment = useCallback(
    (values) => {
      let fieldsToComments = [];
      const {
        findings_in_history,
        observation_in_history,
        assessor_findings,
        assessor_observation,
      } = values;
      const isObservationDefaultValue =
        assessor_observation === OBSERVATION_DEFAULT_VALUE;
      const isFindingsDefaultValue =
        assessor_findings === FINDINGS_DEFAULT_VALUE;
      if (
        observation_in_history &&
        isObservationDefaultValue &&
        findings_in_history &&
        isFindingsDefaultValue
      ) {
        if (checkDefaultComments()) {
          fieldsToComments = ["assessor_findings", "assessor_observation"];
        }
      } else {
        if (
          !isObservationDefaultValue &&
          assessor_observation &&
          observation_in_history
        ) {
          fieldsToComments.push("assessor_observation");
        }
        if (
          !isFindingsDefaultValue &&
          assessor_findings &&
          findings_in_history
        ) {
          fieldsToComments.push("assessor_findings");
        }
      }
      let comment = "";
      if (fieldsToComments.length > 0) {
        fieldsToComments.forEach((field, index) => {
          const isFirst = index === 0;
          let label =
            field === "assessor_observation" ? "Observation" : "Findings";
          comment += `<div class="line-auto fw-400 ${isFirst ? "mb-6" : ""}"><span class="fw-600">${label}</span>: ${values[field]}</div>`;
        });
      }
      if (comment.length > 0) {
        const action = auditActions.addControlComment({
          controlId: currentControl.id,
          comment,
          silent: false,
        });
        dispatch(action);
      }
    },
    [currentControl],
  );

  const handleSubmit = useCallback(() => {
    if (!canEdit) {
      return;
    }
    const values = _formRef.current.getValues();
    const result = {
      report_findings: included,
      assessor_observation: values.assessor_observation,
      assessor_findings: values.assessor_findings,
      result_value: values.result_value,
    };
    const payload = { result, controlId: currentControl.id };
    const action = auditActions.updateControlResult(payload);
    setIsSubmitting(true);
    action.onComplete = () => {
      addComment(values);
      setIsSubmitting(false);
      setDisabled(true);
      publish("UPDATE_AUDIT_FILTER");
    };
    dispatch(action);
  }, [included, canEdit]);

  return (
    <Col className="w-23 ml-20 result-section pa-15">
      <NMForm
        ref={_formRef}
        disabled={!canEdit}
        fields={getFormFields()}
        formData={getFormValues()}
        getOptions={getOptions}
        onChange={handleChange}
      />
      <Row align="center" justify="end" className="w-100">
        <Button
          label="Update"
          icon="SAVE"
          loading={isSubmitting}
          onClick={handleSubmit}
          disabled={disabled}
        />
      </Row>
    </Col>
  );
};

const ControlSection = () => {
  const currentControl = useSelector(getCurrentControl);
  const currentAudit = useSelector(getCurrentAudit);
  const [showInfo, setShowInfo] = useState(null);
  const currentUser = useSelector(getCurrentUser);
  const controlDetails = currentControl
    ? currentAudit.controlMap[currentControl.id]
    : null;

  const handleShowInfo = (fieldName) => {
    const field = controlDetails[fieldName];
    setShowInfo(field);
  };

  const handleCloseInfo = () => {
    setShowInfo(null);
  };

  return (
    <Col className="w-89 ml-20 h-100">
      {currentControl === null && <NMWaitIndicator size="small" />}
      {currentControl === undefined && (
        <Col className="w-100 h-100" align="center" justify="center">
          <span className="fs-10 fw-100 text-subtitle">
            No Control to Display
          </span>
        </Col>
      )}
      {currentControl && (
        <Fragment>
          {controlDetails && (
            <Row>
              <Row className="pl-12 pr-12 pt-6 pb-6 bg-contrast" align="center">
                <span>{controlDetails.group.title}</span>
                <TextButton
                  icon="DOCUMENT_INFO"
                  classes={{ icon: "fs-20", button: "ml-12" }}
                  onClick={() => handleShowInfo("group")}
                />
              </Row>
              <Row
                className="pl-12 pr-12 pt-6 pb-6 bg-contrast ml-10"
                align="center"
              >
                <span>{controlDetails.section.title}</span>
                <TextButton
                  icon="DOCUMENT_INFO"
                  classes={{ icon: "fs-20", button: "ml-12" }}
                  onClick={() => handleShowInfo("section")}
                />
              </Row>
            </Row>
          )}
          <Row className="w-100 mt-12 flex-auto overflow-hide">
            <Row className="w-100 h-100 overflow-auto nm-scroll-view">
              <Row className="w-77 h-100 overflow-hide">
                <Col className="w-63 pr-20 pt-15 pb-15 pl-15 bg-contrast h-100 overflow-hide">
                  <ControlQA />
                  <Evidence />
                </Col>
                <History />
              </Row>
              {!isAuditee(currentUser) && <Result />}
            </Row>
          </Row>
          {showInfo && (
            <NMFormPopup
              visible={true}
              width="814px"
              showClose="label"
              fields={[
                {
                  readOnly: true,
                  attribute: "desc",
                  label: "Description:",
                  typeRepresentation: "TEXTAREA",
                },
              ]}
              formData={{ desc: showInfo.desc }}
              onModalClose={handleCloseInfo}
              classes={{ header: "bg-header pt-8 pb-8", content: "pt-12" }}
              title={
                <span className="fs-15 text-filled fw-500">
                  {showInfo.title}
                </span>
              }
            />
          )}
        </Fragment>
      )}
    </Col>
  );
};

export const AuditViewScreen = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { asmtId } = useParams();
  const _fetchRef = useRef(false);
  const cacheData = useRef({
    controls: [],
    filters: {},
    currentControl: null,
  });
  const currentAudit = useSelector(getCurrentAudit);
  const currentControl = useSelector(getCurrentControl);
  const [state, setState] = useState({
    filteredControls: [],
    filters: {},
  });
  const updatState = (iNewState = {}) => {
    setState((_) => ({ ..._, ...iNewState }));
  };

  useEffect(() => {
    const controls = currentAudit?.asmt_cntrls || [];
    cacheData.current.controls = controls;
  }, [currentAudit?.asmt_cntrls]);

  useEffect(() => {
    cacheData.current.filters = state.filters || {};
  }, [state.filters]);

  useEffect(() => {
    cacheData.current.currentControl = currentControl || null;
  }, [currentControl]);

  useEffect(() => {
    if (!_fetchRef.current) {
      _fetchRef.current = true;
      const action = auditActions.fetchAudit({ auditId: asmtId });
      action.onFailure = (error) => {
        if (error.status === 404) {
          navigate("/page-not-found");
        } else if (error.status === 403) {
          navigate("/restricted");
        }
      };
      action.onComplete = (res) => {
        const controls = res.asmt_cntrls || [];
        cacheData.current.controls = controls;
        setFilterControls(controls, false);
      };
      dispatch(action);
    }
  }, [asmtId]);

  useEffect(() => {}, [currentAudit]);

  const hasFilterValue = useCallback((value) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return value !== undefined && value !== null && value !== "";
  }, []);

  const matchesFilterValue = useCallback(
    (selectedValue, controlValue) => {
      if (!hasFilterValue(selectedValue)) {
        return true;
      }

      if (Array.isArray(selectedValue)) {
        return selectedValue.some(
          (value) => String(value) === String(controlValue ?? ""),
        );
      }

      return String(selectedValue) === String(controlValue ?? "");
    },
    [hasFilterValue],
  );

  const getFilteredControls = useCallback(
    (filters, controls) => {
      return controls.filter((control) => {
        const inDept = matchesFilterValue(
          filters.department,
          control.dept_name,
        );
        const inPriority = matchesFilterValue(
          filters.priority,
          control.priority,
        );
        const inScore = matchesFilterValue(filters.score, control.risk_score);
        const inResult = matchesFilterValue(
          filters.response,
          control.result_value,
        );
        const inStatus = matchesFilterValue(filters.status, control.status);
        return inDept && inPriority && inScore && inResult && inStatus;
      });
    },
    [matchesFilterValue],
  );

  const setFilterControls = useCallback(
    (controls, checkCurrentControl = true) => {
      const filtered = getFilteredControls(cacheData.current.filters, controls);
      updatState({
        filteredControls: filtered,
      });
      if (checkCurrentControl) {
        const currentControlExists = filtered.some(
          (control) => control.id === cacheData.current.currentControl?.id,
        );
        if (filtered.length === 0) {
          dispatch(auditActions.setCurrentControl(undefined));
        } else if (!currentControlExists) {
          dispatch(auditActions.setCurrentControl(null));
          dispatch(auditActions.fetchControl({ controlId: filtered[0].id }));
        }
      }
    },
    [currentControl],
  );

  const handleFilterUpdate = useCallback(() => {
    const filters = cacheData.current.filters || {};
    if (!filters.status && !filters.response) {
      return;
    }
    const timeoutId = setTimeout(() => {
      clearTimeout(timeoutId);
      setFilterControls(cacheData.current.controls);
    }, 0);
  }, [setFilterControls]);

  useSub("UPDATE_AUDIT_FILTER", handleFilterUpdate);

  const handleFilterChange = useCallback(
    (values) => {
      const filters = { ...values };
      cacheData.current.filters = filters;
      const controls = getFilteredControls(filters, cacheData.current.controls);
      updatState({ filteredControls: controls, filters });
      const currentControlExists = controls.some(
        (control) => control.id === currentControl?.id,
      );
      if (!currentControlExists) {
        if (controls.length > 0) {
          dispatch(auditActions.setCurrentControl(null));
          dispatch(auditActions.fetchControl({ controlId: controls[0].id }));
        } else {
          dispatch(auditActions.setCurrentControl(undefined));
        }
      }
    },
    [dispatch, getFilteredControls, currentControl],
  );

  return (
    <Col className="w-100 h-100 overflow-hide audit-view">
      {!currentAudit && <NMWaitIndicator size="small" />}
      {currentAudit && (
        <Fragment>
          <AuditHeader
            onFilterChange={handleFilterChange}
            filteredControls={state.filteredControls}
          />
          <Col
            align="start"
            className="w-100 overflow-hide flex-auto pl-20 pr-20 pt-15 pb-15"
            style={{ backgroundColor: "#D8EFEF" }}
          >
            <Row className="w-100 h-100 overflow-hide">
              <ControlList
                controls={state.filteredControls}
                initialControlId={state.filteredControls[0]?.id}
              />
              <ControlSection />
            </Row>
          </Col>
        </Fragment>
      )}
    </Col>
  );
};
